import {
  createContext,
  useCallback,
  useContext,
  useEffect,
  useId,
  useMemo,
  useState,
  useSyncExternalStore,
  type ReactNode,
} from 'react';
import { Card } from './Card';
import { Modal } from './Modal';
import { HelpIcon } from './HelpIcon';
import { Icon } from '@/lib/icons';
import { cn } from '@/lib/cn';

/**
 * Präfix für den gemerkten Auf-/Zu-Zustand.
 *
 * Der Schlüssel ist bewusst PRO KARTE und NICHT pro Projekt: ein Eintrag je
 * Projekt-und-Karte würde mit jedem geöffneten Projekt weiter wachsen und
 * niemand räumt localStorage je auf. Wer eine Karte zuklappt, meint ohnehin
 * „diese Karte brauche ich generell selten" — nicht „in diesem einen Projekt".
 */
const STORAGE_PREFIX = 'ignisai.card.';

/**
 * Modulweiter Cache + Listener, damit mehrere Karten mit demselben Key
 * synchron bleiben und `useSyncExternalStore` einen stabilen Snapshot bekommt
 * (localStorage direkt zu lesen wäre bei jedem Render ein neuer String-Parse).
 * Gleiches Muster wie in components/layout/Sidebar.tsx.
 */
const collapsedCache = new Map<string, boolean>();
const collapsedListeners = new Set<() => void>();

function readCollapsed(cardKey: string, fallback: boolean): boolean {
  const cached = collapsedCache.get(cardKey);
  if (cached !== undefined) return cached;
  let value = fallback;
  try {
    const raw = window.localStorage.getItem(STORAGE_PREFIX + cardKey);
    if (raw !== null) value = raw === '1';
  } catch {
    /* Storage gesperrt (z. B. Privatmodus) — Fallback bleibt stehen. */
  }
  collapsedCache.set(cardKey, value);
  return value;
}

function writeCollapsed(cardKey: string, collapsed: boolean): void {
  collapsedCache.set(cardKey, collapsed);
  try {
    window.localStorage.setItem(STORAGE_PREFIX + cardKey, collapsed ? '1' : '0');
  } catch {
    /* Storage gesperrt — der Zustand gilt dann nur für diese Sitzung. */
  }
  for (const listener of collapsedListeners) listener();
}

function subscribeCollapsed(onChange: () => void): () => void {
  collapsedListeners.add(onChange);
  return () => {
    collapsedListeners.delete(onChange);
  };
}

/**
 * Klappt eine Karte gezielt auf — von außen, ohne sie zu rendern.
 *
 * Gebraucht für Sprungziele: Eine zugeklappte Karte rendert ihren Inhalt nicht,
 * also läuft ein Anker wie `#comment-123` aus einer Erwähnungs-Benachrichtigung
 * ins Leere, sobald jemand die Kommentar-Karte einmal zugeklappt hat. Der Link
 * scheitert dann still — der Nutzer landet auf der Seite und findet nichts.
 *
 * Wirkt sofort auf bereits gerenderte Karten, weil der Zustand über
 * `useSyncExternalStore` verteilt wird.
 */
export function oeffneKarte(cardKey: string): void {
  writeCollapsed(cardKey, false);
}

/** Gemerkter Auf-/Zu-Zustand einer Karte. */
function useCardCollapsed(
  cardKey: string,
  defaultCollapsed: boolean,
): [boolean, (next: boolean) => void] {
  const collapsed = useSyncExternalStore(
    subscribeCollapsed,
    () => readCollapsed(cardKey, defaultCollapsed),
    () => defaultCollapsed,
  );
  const setCollapsed = useCallback((next: boolean) => writeCollapsed(cardKey, next), [cardKey]);
  return [collapsed, setCollapsed];
}

/* ------------------------------------------------------------------------- *
 * Sammelaktion „alle auf/zu"
 * ------------------------------------------------------------------------- */

interface CollapsibleGroupApi {
  /** Meldet eine Karte an; Rückgabe meldet sie beim Unmount wieder ab. */
  register: (cardKey: string, defaultCollapsed: boolean) => () => void;
}

const CollapsibleGroupContext = createContext<CollapsibleGroupApi | null>(null);

/**
 * Klammer um eine Kartenspalte mit einem Sammel-Umschalter darüber.
 *
 * BEWUSST EIN Umschalter statt zwei Knöpfen: „Alles zuklappen" und „Alles
 * aufklappen" nebeneinander bedeutet, dass einer von beiden fast immer
 * wirkungslos ist — der Nutzer muss dann erst prüfen, welcher gerade etwas
 * tut. Der Umschalter zeigt stattdessen immer genau die Aktion, die etwas
 * bewirkt: alles offen → „Alle zuklappen", sonst → „Alle aufklappen".
 *
 * Die Karten melden sich selbst an, statt dass die Spalte ihre Schlüssel
 * kennt — so wirkt der Umschalter auch auf Karten, die über Skill-Slots
 * dazukommen.
 */
export function CollapsibleCardGroup({
  className,
  children,
}: {
  className?: string;
  children: ReactNode;
}) {
  const [members, setMembers] = useState<ReadonlyMap<string, boolean>>(() => new Map());

  const api = useMemo<CollapsibleGroupApi>(
    () => ({
      register(cardKey, defaultCollapsed) {
        setMembers((prev) => new Map(prev).set(cardKey, defaultCollapsed));
        return () => {
          setMembers((prev) => {
            const next = new Map(prev);
            next.delete(cardKey);
            return next;
          });
        };
      },
    }),
    [],
  );

  // Signatur statt Objekt: useSyncExternalStore vergleicht mit Object.is, ein
  // String ist damit ein stabiler Snapshot — eine neue Map wäre es nicht.
  const signature = useCallback(
    () =>
      Array.from(members)
        .map(([key, fallback]) => (readCollapsed(key, fallback) ? '1' : '0'))
        .join(''),
    [members],
  );
  const state = useSyncExternalStore(subscribeCollapsed, signature, signature);

  const anyCollapsed = state.includes('1');
  const label = anyCollapsed ? 'Alle aufklappen' : 'Alle zuklappen';

  // Der Provider muss IMMER rendern — solange keine Karte angemeldet ist,
  // fehlt nur der Umschalter; ohne Kontext könnte sich nie eine anmelden.
  return (
    <CollapsibleGroupContext.Provider value={api}>
      <div className={className}>
        {members.size > 0 ? (
          <div className="flex justify-end">
            <button
              type="button"
              aria-label={`${label} (${members.size} Karten)`}
              onClick={() => {
                for (const key of members.keys()) writeCollapsed(key, !anyCollapsed);
              }}
              className="inline-flex items-center gap-1 rounded px-1.5 py-0.5 text-xs text-ink-500 hover:bg-ink-100 hover:text-ink-800 focus:outline-none focus:ring-1 focus:ring-accent-500"
            >
              <Icon name={anyCollapsed ? 'ChevronDown' : 'ChevronUp'} size={13} />
              {label}
            </button>
          </div>
        ) : null}
        {children}
      </div>
    </CollapsibleGroupContext.Provider>
  );
}

/* ------------------------------------------------------------------------- *
 * Einzelkarte
 * ------------------------------------------------------------------------- */

export interface CollapsibleCardProps {
  /**
   * Stabiler Schlüssel für den gemerkten Zustand — z. B. `projekt.eckdaten`.
   * Nicht die Projekt-ID einbauen (siehe STORAGE_PREFIX).
   */
  cardKey: string;
  /** Überschrift; dient auch als Titel der Groß-Ansicht. */
  title: string;
  /** Tooltip-Inventory-Key für das Hilfe-Icon neben der Überschrift. */
  helpKey?: string;
  /**
   * Kernaussage der Karte. Bleibt im zugeklappten Zustand sichtbar, damit man
   * nicht aufklappen muss, um zu sehen, ob die Karte etwas zu sagen hat
   * (z. B. „1 Abweichung").
   */
  summary?: ReactNode;
  /** Aktion der Karte (Button/Badge). Nur im aufgeklappten Zustand sichtbar. */
  action?: ReactNode;
  /** Startzustand, solange nichts gemerkt ist. */
  defaultCollapsed?: boolean;
  /** Groß-Ansicht anbieten. Aus, wenn der Inhalt seine eigene hat (z. B. Karte). */
  enlargeable?: boolean;
  /** Zusatztext im Kopf der Groß-Ansicht. */
  enlargeDescription?: string;
  className?: string;
  children: ReactNode;
}

/**
 * Karte mit Ein-/Ausklappen und Groß-Ansicht.
 *
 * Beides ist optional aufgesetzt: die Basis bleibt `Card`, die Groß-Ansicht ist
 * das bestehende `Modal` (inkl. ESC + Fokusfalle) — kein drittes Overlay-Muster.
 *
 * Der Inhalt wird bewusst nur an EINER Stelle gemountet: entweder inline oder
 * in der Groß-Ansicht. Zwei parallele Instanzen desselben Teilbaums würden
 * Dialoge und Fokusfallen doppelt in den DOM legen.
 */
export function CollapsibleCard({
  cardKey,
  title,
  helpKey,
  summary,
  action,
  defaultCollapsed = false,
  enlargeable = true,
  enlargeDescription,
  className,
  children,
}: CollapsibleCardProps) {
  const [collapsed, setCollapsed] = useCardCollapsed(cardKey, defaultCollapsed);
  const [enlarged, setEnlarged] = useState(false);
  const bodyId = useId();

  // An-/Abmelden bei der umgebenden Spalte, damit „Alle auf/zu" diese Karte
  // erfasst. Ohne Gruppe (Einzelverwendung) passiert schlicht nichts.
  const group = useContext(CollapsibleGroupContext);
  useEffect(() => group?.register(cardKey, defaultCollapsed), [group, cardKey, defaultCollapsed]);

  return (
    <>
      <Card className={className}>
        <div className="flex items-center gap-1">
          <button
            type="button"
            onClick={() => setCollapsed(!collapsed)}
            aria-expanded={!collapsed}
            aria-controls={bodyId}
            className="-mx-1 flex min-w-0 flex-1 items-center gap-1.5 rounded px-1 py-0.5 text-left hover:bg-ink-50 focus:outline-none focus:ring-1 focus:ring-accent-500"
          >
            <Icon
              name="ChevronDown"
              size={14}
              className={cn(
                'text-ink-400 transition-transform duration-150',
                collapsed && '-rotate-90',
              )}
            />
            <span className="truncate text-xs font-medium uppercase tracking-wide text-ink-500">
              {title}
            </span>
          </button>
          {helpKey ? <HelpIcon helpKey={helpKey} /> : null}
          {enlargeable ? (
            <button
              type="button"
              onClick={() => setEnlarged(true)}
              aria-label={`${title} groß anzeigen`}
              title="Groß anzeigen"
              className="-m-1 rounded p-1 text-ink-400 hover:bg-ink-100 hover:text-ink-800 focus:outline-none focus:ring-1 focus:ring-accent-500"
            >
              <Icon name="Expand" size={14} />
            </button>
          ) : null}
        </div>

        <div id={bodyId}>
          {collapsed ? (
            summary ? (
              <div className="mt-2 text-xs text-ink-600">{summary}</div>
            ) : null
          ) : enlarged ? (
            // Der Inhalt ist gerade in die Groß-Ansicht gewandert (nur ein
            // Mount, s. Komponenten-Kommentar). Der Platzhalter hält die Karte
            // begehbar, statt sie auf Kopfhöhe zusammenfallen zu lassen.
            <p className="mt-2 text-xs text-ink-500">Inhalt wird gerade groß angezeigt.</p>
          ) : (
            <>
              {action ? <div className="mt-2 flex justify-end">{action}</div> : null}
              <div className="mt-3">{children}</div>
            </>
          )}
        </div>
      </Card>

      <Modal
        open={enlarged}
        onClose={() => setEnlarged(false)}
        title={title}
        description={enlargeDescription}
        size="xl"
      >
        {action ? <div className="mb-3 flex justify-end">{action}</div> : null}
        {/* Höhe deckeln, damit lange Inhalte (Verlauf, Prüfbefunde) im Dialog
            scrollen statt ihn über den Bildschirmrand zu schieben. */}
        <div className="max-h-[70vh] overflow-y-auto">{children}</div>
      </Modal>
    </>
  );
}
